import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

const FinanceCapabilities = ({ agentId, showNotification }) => {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchCapabilities();
  }, [agentId]);

  const fetchCapabilities = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/ai/agents/${agentId}/capabilities/finance`);
      if (response.data.success) {
        setCapabilities(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching finance capabilities:', error);
      showNotification?.('Failed to load finance capabilities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put(
        `/api/ai/agents/${agentId}/capabilities/finance`,
        capabilities
      );
      if (response.data.success) {
        showNotification?.('Finance capabilities saved successfully', 'success');
        setHasChanges(false);
      }
    } catch (error) {
      logger.error('Error saving finance capabilities:', error);
      showNotification?.('Failed to save finance capabilities', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    fetchCapabilities();
    setHasChanges(false);
  };

  const updateCapability = (path, value) => {
    setCapabilities(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!capabilities) {
    return <div className="p-4 text-gray-500">No finance capabilities configured</div>;
  }

  return (
    <div className="space-y-6">
      {/* Master Enable Toggle */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={capabilities.enabled || false}
            onChange={(e) => updateCapability('enabled', e.target.checked)}
            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="ml-3 font-medium text-gray-900 dark:text-white">
            Enable Finance Capabilities
          </span>
        </label>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Enable this agent to handle finance-related tasks including invoices, expenses, budgets, and reporting.
        </p>
      </div>

      {capabilities.enabled && (
        <>
          {/* Invoice Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Invoice Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.invoiceManagement?.enabled || false}
                  onChange={(e) => updateCapability('invoiceManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable invoice management</span>
              </label>

              {capabilities.invoiceManagement?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.invoiceManagement?.autoApprove || false}
                      onChange={(e) => updateCapability('invoiceManagement.autoApprove', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-approve invoices</span>
                  </label>

                  <div>
                    <label className="block text-sm font-medium mb-1">Approval Threshold</label>
                    <input
                      type="number"
                      value={capabilities.invoiceManagement?.approvalThreshold || 1000}
                      onChange={(e) => updateCapability('invoiceManagement.approvalThreshold', Number(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-[#222222]"
                      placeholder="1000"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-approve invoices below this amount</p>
                  </div>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.invoiceManagement?.requireDualApproval || false}
                      onChange={(e) => updateCapability('invoiceManagement.requireDualApproval', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Require dual approval for high-value invoices</span>
                  </label>

                  <div>
                    <label className="block text-sm font-medium mb-1">Payment Terms</label>
                    <label className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={capabilities.invoiceManagement?.paymentTerms?.enabled || false}
                        onChange={(e) => updateCapability('invoiceManagement.paymentTerms.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Enable payment terms</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Expense Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Expense Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.expenseManagement?.enabled || false}
                  onChange={(e) => updateCapability('expenseManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable expense management</span>
              </label>

              {capabilities.expenseManagement?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.expenseManagement?.autoApprove || false}
                      onChange={(e) => updateCapability('expenseManagement.autoApprove', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-approve expense reports</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.expenseManagement?.reimbursementWorkflow || false}
                      onChange={(e) => updateCapability('expenseManagement.reimbursementWorkflow', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enable reimbursement workflow</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Budget Tracking */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Budget Tracking</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.budgetTracking?.enabled || false}
                  onChange={(e) => updateCapability('budgetTracking.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable budget tracking</span>
              </label>

              {capabilities.budgetTracking?.enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Alert Threshold (%)</label>
                    <input
                      type="number"
                      value={capabilities.budgetTracking?.alertThreshold || 80}
                      onChange={(e) => updateCapability('budgetTracking.alertThreshold', Number(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-[#222222]"
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Alert when budget usage exceeds this percentage</p>
                  </div>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.budgetTracking?.autoBlockOverspend || false}
                      onChange={(e) => updateCapability('budgetTracking.autoBlockOverspend', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Automatically block overspending</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Financial Reporting */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Financial Reporting</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.reporting?.enabled || false}
                  onChange={(e) => updateCapability('reporting.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable financial reporting</span>
              </label>

              {capabilities.reporting?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.reporting?.autoGenerateReports || false}
                      onChange={(e) => updateCapability('reporting.autoGenerateReports', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-generate reports</span>
                  </label>

                  <div>
                    <label className="block text-sm font-medium mb-1">Financial Period</label>
                    <select
                      value={capabilities.reporting?.financialPeriod || 'monthly'}
                      onChange={(e) => updateCapability('reporting.financialPeriod', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-[#222222]"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Integrations */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Integrations</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Accounting System</label>
                <select
                  value={capabilities.integrations?.accountingSystem || ''}
                  onChange={(e) => updateCapability('integrations.accountingSystem', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-[#222222]"
                >
                  <option value="">None</option>
                  <option value="quickbooks">QuickBooks</option>
                  <option value="xero">Xero</option>
                  <option value="freshbooks">FreshBooks</option>
                  <option value="sage">Sage</option>
                </select>
              </div>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.integrations?.bankingIntegration || false}
                  onChange={(e) => updateCapability('integrations.bankingIntegration', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable banking integration</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.integrations?.taxCompliance || false}
                  onChange={(e) => updateCapability('integrations.taxCompliance', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable tax compliance checks</span>
              </label>
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-[#1a1a1a] p-4 border-t border-gray-200 dark:border-gray-800 -mx-6 -mb-6">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default FinanceCapabilities;
